import { convertComparisonTypeToString } from '@myosh/odin-components';
import type { ContinuousScrollerFilter, ContinuousScrollerSort, OdinIconName } from '@myosh/odin-components';
import type { FieldFilters, FieldSorts } from '../../../@types/common';
import type { FilterCustomProperties } from '../../../@types/form-filters';
import { forceAssert } from '../../../utils/type-guards';

//API returned values for menu items
export const RecordListMenuItemType = {
  Restore: 'RESTORE_CURRENT_VIEW',
  SaveView: 'SAVE_CURRENT_VIEW',
  ManageView: 'MANAGE_VIEW',
  CalendarView: 'CALENDAR_VIEW',
  ImportAttachments: 'IMPORT_ATTACHMENTS',
  Import: 'IMPORT_RECORDS',
  Export: 'EXPORT_RECORDS',
  Addon: 'ADD_ON',
  AnalyzeRecord: 'ANALYSE_RECORDS',
} as const;
export type RecordListMenuItemType = (typeof RecordListMenuItemType)[keyof typeof RecordListMenuItemType];

export interface DataGridSettingsMenuItem {
  key: string;
  label?: string;
  icon?: OdinIconName;
  url?: string;
  target?: string;
  subMenuItems?: Array<DataGridSettingsMenuItem>;
  isSeparator?: boolean;
  visible?: boolean;
  disabled?: boolean;
  isPinned?: boolean;
  isPinIconHidden?: boolean;
  addonId?: number;
  onClick?: () => void;
  className?: string;
  isTitle?: boolean;
  titleDescription?: string;
}

export const convertToSortedFields = (arrOfSorts: ContinuousScrollerSort[], useFieldAsKey = false): FieldSorts => {
  const result: FieldSorts = {};

  arrOfSorts.forEach((item) => {
    const field = useFieldAsKey ? item.field : item.scrollerId;

    // Skip items where field is undefined to prevent TypeScript indexing errors.
    // This is expected behavior in the following scenarios:
    //   1. During grid initialization before sort configuration is fully loaded from API
    //   2. When user clears/resets sorting but state update hasn't completed
    //   3. With dynamic columns where scrollerId may be temporarily undefined
    //   4. In partial sort configurations where some items lack field identifiers
    // Undefined fields are silently skipped rather than throwing errors to maintain
    // grid functionality with incomplete data. No logging needed as this is normal.
    if (field === undefined) {
      return;
    }

    const direction = item.sortDirection === 0 ? 'asc' : 'desc';
    const order = item.order ?? 0;
    const customDataProperties = item.customDataProperties;

    result[field] = {
      direction,
      order,
      customDataProperties,
    };
  });

  return result;
};

export const convertToFieldFilters = (filters: ContinuousScrollerFilter[]): FieldFilters => {
  const fieldFilters: FieldFilters = {};

  filters.forEach((filter) => {
    const { customDataProperties = {} } = filter;
    const { entityPropertyName, entityType } = forceAssert<FilterCustomProperties>(customDataProperties);
    if (filter.value !== undefined && filter.value !== null) {
      let comparison = '';
      const key = entityType === 'KEYWORD' ? entityPropertyName : filter.scrollerId;
      if (filter.comparison !== undefined && filter.comparison !== null) {
        comparison = convertComparisonTypeToString(filter.comparison);
      }
      if (Array.isArray(filter.value)) {
        const sanitizedArray = filter.value.filter((item) => item !== undefined && item !== null);
        if (sanitizedArray.length > 0) {
          fieldFilters[key] = {
            value: sanitizedArray,
            comparison,
            customDataProperties: filter.customDataProperties,
          };
        }
      } else if (filter.value instanceof Date && !isNaN(filter.value.getTime())) {
        fieldFilters[key] = {
          value: filter.value,
          comparison,
          customDataProperties: filter.customDataProperties,
        };
      } else {
        fieldFilters[key] = {
          value: filter.value,
          comparison,
          customDataProperties: filter.customDataProperties,
        };
      }
    }
  });

  return fieldFilters;
};
